/**
 * Sentry React SDK init for the portal SPA.
 *
 * Mirrors the api Worker's "no DSN = no telemetry" pattern
 * (`@feluda/telemetry`'s `isTelemetryEnabled`). When `VITE_SENTRY_DSN`
 * is unset, `initSentry()` is a no-op — the rest of the SPA imports
 * the module unconditionally so the call sites stay terse.
 *
 * DSN source: the user provisions a dedicated Sentry React project
 * (separate from the api/indexer/reviewer Workers project) and sets
 * the DSN in `apps/portal/.env` as `VITE_SENTRY_DSN=…`. Vite inlines
 * `VITE_*` env vars into the build; the value is public-by-design but
 * we keep it out of git via `.env` (gitignored).
 *
 * Integrations:
 *   - `browserTracingIntegration` — distributed tracing for React
 *     Router navigations (matches the api Worker's `withSentry`).
 *
 * Tracing policy:
 *   - `tracesSampleRate: 1.0` in dev (full visibility); production
 *     uses `0.1` via `VITE_SENTRY_TRACES_SAMPLE_RATE` override.
 *   - `tracePropagationTargets` includes `/api` so requests through
 *     Vite's dev proxy (and same-origin in prod) carry the trace
 *     header through to the api Worker.
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const SENTRY_TRACES_SAMPLE_RATE = Number(
  (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ?? '0.1',
);
const SENTRY_ENVIRONMENT = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  (import.meta.env.DEV ? 'development' : 'production');

export const isSentryEnabled = (): boolean => Boolean(SENTRY_DSN);

export function initSentry(): void {
  if (!SENTRY_DSN) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    tracePropagationTargets: ['localhost', /^https?:\/\/[^/]+\/api/],
    // Disable PII by default — the portal handles installation ids and
    // repo metadata, which Sentry should never see. Operators can flip
    // these via the Sentry dashboard if they need them for a session.
    sendDefaultPii: false,
  });
}

export { Sentry };